import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  useMediaQuery,
  useTheme,
  IconButton,
  Link,
} from "@mui/material";
import { X, ArrowRight, ArrowLeft } from "lucide-react";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { useSnackbar } from "notistack";
import sh from "bundles/common/utils/sh";
import { countryMap } from "bundles/common/utils/constants";
import PhoneInput from "react-phone-input-2";
import { FormattedMessage, useIntl } from "react-intl";
import { trackDownloadPricing } from "../../common/utils/marketing";

const PricingModal = ({ closeModal }) => {
  const steps = [
    { index: 0, key: 1, finale: false },
    { index: 1, key: 2, finale: false },
    { index: 2, key: 3, finale: true },
  ];

  const theme = useTheme();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const stepTransition = isDesktop ? 600 : 240;

  const defaultPhoneValue = "+32 490 12 34 56";
  const initialFormValues = {
    last_name: "",
    first_name: "",
    email: "",
    phone: defaultPhoneValue,
    job_title: "",
    company_name: "",
    recruitment_plan: "",
  };

  const stepValues = {
    1: ["first_name", "last_name"],
    2: ["email", "phone"],
    3: ["company_name", "job_title", "recruitment_plan"],
  };

  const [values, setValues] = useState(initialFormValues);
  const [currentStep, setCurrentStep] = useState(0);
  const [previousStep, setPreviousStep] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [phoneError, setPhoneError] = useState(null);
  const railsEnv = useSelector((state) => state.railsEnv);

  const handleInputValue = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const handlePhoneInputClick = () => {
    if (values.phone === defaultPhoneValue)
      setValues({ ...values, ["phone"]: "" });
  };

  const onPlanSelected = (plan) => {
    setSelectedPlan(plan);
    setValues({ ...values, ["recruitment_plan"]: plan });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let step = steps.find((step) => step.index == currentStep);

    if (step.finale) {
      sh.post(`/${i18nLocale}/pricing-lead`, {
        data: values,
      })
        .then(() => {
          let locale = i18nLocale || "en";
          // eslint-disable-next-line no-undef
          let url = `https://squarehub-production.s3.${process.env.AWS_REGION}.amazonaws.com/docs/squarehub_2025_${locale}.pdf`;
          let link = document.createElement("a");
          link.href = url;
          link.download = url;
          link.target = "_blank";
          link.dispatchEvent(new MouseEvent("click"));
          enqueueSnackbar(
            intl.formatMessage({ id: "pricing_modal.success_snack" }),
            { variant: "success" }
          );
          if (railsEnv === "production") trackDownloadPricing();
          setTimeout(() => {
            closeModal();
          }, 3000);
        })
        .catch(() => {
          enqueueSnackbar(
            intl.formatMessage({ id: "pricing_modal.failed_snack" }),
            { variant: "error" }
          );
        });
    } else {
      if (currentStep == 1 && values.phone == defaultPhoneValue) {
        setPhoneError("default_phone_error");
      } else {
        setPreviousStep(step.index);
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const goBack = () => {
    let step = currentStep;
    if (step > 0) {
      setPreviousStep(step);
      setCurrentStep(step - 1);
    }
  };

  function hasStepValue(step) {
    let currentValues = Object.keys(values).filter(
      (v) => values[v] != "" && values[v] != null
    );
    return currentValues.some((element) => {
      return stepValues[step].includes(element);
    });
  }

  function hasCompletedStep(step) {
    let currentValues = Object.keys(values).filter(
      (v) => values[v] != "" && values[v] != null
    );
    return (
      currentValues.length > 0 &&
      stepValues[step].every((element) => {
        if (
          step == 2 &&
          (values.phone === defaultPhoneValue ||
            !values.phone
              .replaceAll(" ", "")
              .match("^[+]?[(]?[0-9]{3}[)]?[-s.]?[0-9]{3}[-s.]?[0-9]{4,6}$"))
        )
          return false;
        return currentValues.includes(element);
      })
    );
  }

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: "hsla(0, 0%, 100%, 0.06)",
      borderRadius: "0.5rem",
      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
      fontSize: "0.875rem",
      color: "hsl(0 0% 100%)",
      "& fieldset": { border: "1px solid hsl(0 0% 100% / 0.12)" },
      "&:hover fieldset": { border: "1px solid hsl(0 0% 100% / 0.25)" },
      "&.Mui-focused fieldset": {
        border: "2px solid hsl(153 68% 25%)",
      },
    },
    "& .MuiOutlinedInput-input": {
      color: "hsl(0 0% 100%)",
      "&::placeholder": {
        color: "hsl(0 0% 100% / 0.4)",
        opacity: 1,
      },
    },
    "& .MuiInputLabel-root": {
      color: "hsl(0 0% 100% / 0.5)",
      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
      fontSize: "0.875rem",
      "&.Mui-focused": {
        color: "hsl(153 68% 25%)",
      },
    },
  };

  const primaryBtnSx = {
    bgcolor: "hsl(153 68% 25%)",
    color: "white",
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
    fontWeight: 700,
    fontSize: "0.875rem",
    px: "1.5rem",
    py: "0.875rem",
    borderRadius: "0.75rem",
    textTransform: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    "&:hover": {
      bgcolor: "hsl(153 68% 25% / 0.9)",
    },
    "&.Mui-disabled": {
      bgcolor: "hsl(153 68% 25% / 0.3)",
      color: "hsl(0 0% 100% / 0.4)",
    },
  };

  const secondaryBtnSx = {
    bgcolor: "transparent",
    color: "hsl(0 0% 100% / 0.7)",
    border: "1px solid hsl(0 0% 100% / 0.15)",
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
    fontWeight: 700,
    fontSize: "0.875rem",
    px: "1.5rem",
    py: "0.875rem",
    borderRadius: "0.75rem",
    textTransform: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    "&:hover": {
      bgcolor: "hsl(0 0% 100% / 0.06)",
      borderColor: "hsl(0 0% 100% / 0.3)",
    },
  };

  const StepIndicator = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        gap: "12px",
        mb: { xs: "32px", md: "48px" },
      }}
    >
      {steps.map((step) => (
        <Box
          key={step.key}
          sx={{
            width: "40px",
            height: "4px",
            borderRadius: "2px",
            bgcolor:
              step.index <= currentStep
                ? "hsl(153 68% 25%)"
                : "hsl(0 0% 100% / 0.15)",
            transition: "background-color 0.3s ease",
          }}
        />
      ))}
    </Box>
  );

  function StepOne(x) {
    return (
      <motion.div
        initial={{ x: x }}
        transition={{ duration: 0.5 }}
        animate={{ x: 0 }}
      >
        <Grid container spacing={2}>
          <Grid item xs={isDesktop ? 6 : 12}>
            <TextField
              sx={inputSx}
              required
              autoFocus
              fullWidth
              variant="outlined"
              label={
                <FormattedMessage id="pricing_modal.step_one.first_name" />
              }
              name="first_name"
              value={values.first_name}
              onBlur={handleInputValue}
              onChange={handleInputValue}
            />
          </Grid>
          <Grid item xs={isDesktop ? 6 : 12}>
            <TextField
              sx={inputSx}
              required
              fullWidth
              variant="outlined"
              label={<FormattedMessage id="pricing_modal.step_one.last_name" />}
              name="last_name"
              value={values.last_name}
              onBlur={handleInputValue}
              onChange={handleInputValue}
            />
          </Grid>
          <Grid
            item
            xs={isDesktop ? 6 : 0}
            sx={{ p: isDesktop ? "8px" : "0px" }}
          />
          <Grid
            item
            xs={isDesktop ? 6 : 12}
            sx={{ justifyContent: "end", display: "flex" }}
          >
            <Button
              type="submit"
              disabled={!hasCompletedStep(1)}
              sx={{
                ...primaryBtnSx,
                minWidth: isDesktop ? "50%" : "100%",
              }}
            >
              <FormattedMessage id="pricing_modal.next_button" />
              <ArrowRight size={14} />
            </Button>
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              textAlign: "center",
              mt: "8px",
              mb: "8px",
            }}
          >
            <Box
              component="p"
              sx={{
                m: 0,
                fontSize: "0.75rem",
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                color: "hsl(0 0% 100% / 0.4)",
              }}
            >
              <FormattedMessage id="pricing_modal.required" />
            </Box>
          </Grid>
        </Grid>
      </motion.div>
    );
  }

  function StepTwo(x) {
    return (
      <motion.div
        initial={{ x: x }}
        transition={{ duration: 0.5 }}
        animate={{ x: 0 }}
      >
        <Grid container spacing={2}>
          <Grid item xs={isDesktop ? 6 : 12}>
            <TextField
              sx={inputSx}
              required
              fullWidth
              variant="outlined"
              type="email"
              name="email"
              value={values.email}
              label={<FormattedMessage id="pricing_modal.step_two.email" />}
              onBlur={handleInputValue}
              onChange={handleInputValue}
            />
          </Grid>
          <Grid item xs={isDesktop ? 6 : 12}>
            <PhoneInput
              country={"be"}
              onlyCountries={countryMap.values}
              inputStyle={{
                height: "48px",
                minWidth: "100%",
                backgroundColor: "hsla(0, 0%, 100%, 0.06)",
                border: "1px solid hsla(0, 0%, 100%, 0.12)",
                borderRadius: "0.5rem",
                color:
                  values.phone == defaultPhoneValue
                    ? "hsla(0, 0%, 100%, 0.4)"
                    : "white",
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                fontSize: "0.875rem",
              }}
              buttonStyle={{
                backgroundColor: "hsla(0, 0%, 100%, 0.06)",
                border: "1px solid hsla(0, 0%, 100%, 0.12)",
                borderRadius: "0.5rem 0 0 0.5rem",
              }}
              dropdownStyle={{
                backgroundColor: "hsl(178 93% 10%)",
                color: "white",
              }}
              value={values.phone}
              placeholder="+32 490 12 34 56"
              inputProps={{
                name: "phone",
                required: true,
                autoFocus: false,
                onBlur: handleInputValue,
                onChange: handleInputValue,
                onClick: handlePhoneInputClick,
              }}
            />
            {phoneError && (
              <Box
                component="p"
                sx={{
                  m: 0,
                  mt: "4px",
                  fontSize: "0.75rem",
                  color: "hsl(0, 70%, 60%)",
                  fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                }}
              >
                Please enter your phone number
              </Box>
            )}
          </Grid>
          {isDesktop && (
            <Grid item xs={6}>
              <Button
                type="button"
                onClick={goBack}
                sx={{
                  ...secondaryBtnSx,
                  minWidth: isDesktop ? "50%" : "100%",
                }}
              >
                <ArrowLeft size={14} />
                <FormattedMessage id="pricing_modal.previous_button" />
              </Button>
            </Grid>
          )}
          <Grid
            item
            xs={isDesktop ? 6 : 12}
            sx={{ justifyContent: "end", display: "flex" }}
          >
            <Button
              type="submit"
              disabled={!hasCompletedStep(2)}
              sx={{
                ...primaryBtnSx,
                minWidth: isDesktop ? "50%" : "100%",
              }}
            >
              <FormattedMessage id="pricing_modal.next_button" />
              <ArrowRight size={14} />
            </Button>
          </Grid>
          {!isDesktop && (
            <Grid item xs={12}>
              <Button
                type="button"
                onClick={goBack}
                sx={{
                  ...secondaryBtnSx,
                  minWidth: "100%",
                }}
              >
                <ArrowLeft size={14} />
                <FormattedMessage id="pricing_modal.previous_button" />
              </Button>
            </Grid>
          )}
          <Grid item xs={12} sx={{ textAlign: "center" }}>
            <Box
              component="p"
              sx={{
                m: 0,
                fontSize: "0.75rem",
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                color: "hsl(0 0% 100% / 0.4)",
              }}
            >
              <FormattedMessage id="pricing_modal.required" />
            </Box>
          </Grid>
        </Grid>
      </motion.div>
    );
  }

  function StepThree(x) {
    return (
      <motion.div
        initial={{ x: x }}
        transition={{ duration: 0.5 }}
        animate={{ x: 0 }}
      >
        <Grid container spacing={2}>
          <Grid item xs={isDesktop ? 6 : 12}>
            <TextField
              sx={inputSx}
              required
              fullWidth
              variant="outlined"
              label={
                <FormattedMessage id="pricing_modal.step_three.company_name" />
              }
              name="company_name"
              value={values.company_name}
              onBlur={handleInputValue}
              onChange={handleInputValue}
            />
          </Grid>
          <Grid item xs={isDesktop ? 6 : 12}>
            <TextField
              sx={inputSx}
              required
              fullWidth
              variant="outlined"
              label={
                <FormattedMessage id="pricing_modal.step_three.job_title" />
              }
              name="job_title"
              value={values.job_title}
              onBlur={handleInputValue}
              onChange={handleInputValue}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              mt: isDesktop ? "32px" : "8px",
              mb: isDesktop ? "16px" : "8px",
              textAlign: "center",
            }}
          >
            <Box
              component="p"
              sx={{
                m: 0,
                fontSize: "1rem",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                color: "hsl(0 0% 100% / 0.85)",
              }}
            >
              <FormattedMessage id="pricing_modal.step_three.recruitment_plan" />
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              ml: isDesktop ? "28%" : "0px",
              mr: isDesktop ? "28%" : "0px",
              mb: isDesktop ? "32px" : "8px",
            }}
          >
            <Grid container spacing={2}>
              {["1-4", "5-10", "11+"].map((plan) => (
                <Grid
                  key={plan}
                  item
                  xs={4}
                  sx={{ display: "flex", justifyContent: "center" }}
                >
                  <Box
                    onClick={() => onPlanSelected(plan)}
                    sx={{
                      height: "48px",
                      display: "flex",
                      border:
                        selectedPlan === plan
                          ? "1px solid hsl(153 68% 25%)"
                          : "1px solid hsl(0 0% 100% / 0.15)",
                      borderRadius: "0.75rem",
                      cursor: "pointer",
                      alignItems: "center",
                      justifyContent: "center",
                      color:
                        selectedPlan === plan
                          ? "white"
                          : "hsl(0 0% 100% / 0.7)",
                      bgcolor:
                        selectedPlan === plan
                          ? "hsl(153 68% 25%)"
                          : "transparent",
                      width: isDesktop ? "120px" : "80px",
                      fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                      fontSize: "0.875rem",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor:
                          selectedPlan === plan
                            ? "hsl(153 68% 25%)"
                            : "hsl(0 0% 100% / 0.3)",
                        bgcolor:
                          selectedPlan === plan
                            ? "hsl(153 68% 25% / 0.9)"
                            : "hsl(0 0% 100% / 0.04)",
                      },
                    }}
                  >
                    {plan}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {isDesktop && (
            <Grid item xs={6}>
              <Button
                type="button"
                onClick={goBack}
                sx={{
                  ...secondaryBtnSx,
                  minWidth: isDesktop ? "50%" : "100%",
                }}
              >
                <ArrowLeft size={14} />
                <FormattedMessage id="pricing_modal.previous_button" />
              </Button>
            </Grid>
          )}
          <Grid
            item
            xs={isDesktop ? 6 : 12}
            sx={{ justifyContent: "end", display: "flex" }}
          >
            <Button
              type="submit"
              disabled={!hasCompletedStep(3)}
              sx={{
                ...primaryBtnSx,
                minWidth: isDesktop ? "50%" : "100%",
              }}
            >
              <FormattedMessage id="pricing_modal.download_button" />
              <ArrowRight size={14} />
            </Button>
          </Grid>
          {!isDesktop && (
            <Grid item xs={12}>
              <Button
                type="button"
                onClick={goBack}
                sx={{
                  ...secondaryBtnSx,
                  minWidth: "100%",
                }}
              >
                <ArrowLeft size={14} />
                <FormattedMessage id="pricing_modal.previous_button" />
              </Button>
            </Grid>
          )}

          <Grid
            item
            xs={12}
            sx={{
              textAlign: isDesktop ? "left" : "center",
              mt: isDesktop ? "16px" : "4px",
            }}
          >
            <Box
              component="p"
              sx={{
                m: 0,
                fontSize: "0.75rem",
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                color: "hsl(0 0% 100% / 0.4)",
              }}
            >
              {intl.formatMessage(
                { id: "pricing_modal.step_three.disclaimer" },
                {
                  a: (str) => (
                    <Link
                      href="privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "hsl(153 68% 25%)",
                        fontWeight: 700,
                        textDecoration: "underline",
                        textDecorationColor: "hsl(153 68% 25% / 0.4)",
                        "&:hover": {
                          textDecorationColor: "hsl(153 68% 25%)",
                        },
                      }}
                    >
                      {str}
                    </Link>
                  ),
                }
              )}
            </Box>
          </Grid>
        </Grid>
      </motion.div>
    );
  }

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        overflow: "auto",
        display: "flex",
        top: 0,
        bottom: 0,
        zIndex: 999,
        p: "32px",
        bgcolor: "hsl(178 93% 10%)",
      }}
    >
      {/* Close button */}
      <Box sx={{ position: "absolute", top: "16px", right: "16px" }}>
        <IconButton
          edge="end"
          aria-label="close"
          onClick={() => closeModal()}
          sx={{
            color: "hsl(0 0% 100% / 0.6)",
            "&:hover": {
              color: "hsl(0 0% 100%)",
              bgcolor: "hsl(0 0% 100% / 0.06)",
            },
          }}
        >
          <X size={20} />
        </IconButton>
      </Box>

      <form
        onSubmit={handleSubmit}
        style={{
          marginTop: "auto",
          marginBottom: "auto",
          overflow: "hidden",
          marginLeft: isDesktop ? "auto" : "0px",
          marginRight: isDesktop ? "auto" : "0px",
          width: isDesktop ? "50%" : "100%",
        }}
      >
        {/* Step indicators */}
        <StepIndicator />

        {/* Title */}
        <Box
          component="h2"
          sx={{
            fontSize: { xs: "1.5rem", md: "1.875rem" },
            fontFamily: "Segoe UI Black, system-ui, sans-serif",
            color: "hsl(0 0% 100%)",
            letterSpacing: "-0.025em",
            textAlign: "center",
            mb: { xs: "24px", md: "48px" },
            mt: 0,
          }}
        >
          <FormattedMessage id="pricing_modal.title" />
        </Box>

        {/* Step content */}
        <Box sx={{ display: "flex" }}>
          {currentStep === 0 && (
            <Box sx={{ flex: "0 0 auto", width: "100%", mt: isDesktop ? "0px" : "32px" }}>
              {StepOne(hasStepValue(1) ? -stepTransition : 0)}
            </Box>
          )}
          {currentStep === 1 && (
            <Box sx={{ flex: "0 0 auto", width: "100%", mt: isDesktop ? "0px" : "32px" }}>
              {StepTwo(
                !hasStepValue(2)
                  ? stepTransition
                  : previousStep < currentStep
                    ? stepTransition
                    : -stepTransition
              )}
            </Box>
          )}
          {currentStep === 2 && (
            <Box sx={{ flex: "0 0 auto", width: "100%", mt: isDesktop ? "0px" : "32px" }}>
              {StepThree(stepTransition)}
            </Box>
          )}
        </Box>
      </form>
    </Box>
  );
};

export default PricingModal;
